// src/views/admin/ADNotifications.jsx
//
// PERMISSIONS (module "notifications") :
// - notifications.voir                : accès à la page (déjà géré sidebar / route)
//
// Onglet Notifications :
// - notifications.envoyer             : bouton "Nouvelle notification"
// - notifications.notifier_absences   : bouton "Notifier les absences"
// - notifications.supprimer           : bouton "Supprimer" d'une notification
//
// Onglet Notes d'information :
// - notifications.creer_note          : bouton "Nouvelle note"
// - notifications.modifier_note       : bouton "Modifier"
// - notifications.supprimer_note      : bouton "Supprimer"
// - notifications.imprimer_note       : boutons "Imprimer" et "PDF"
//   (l'aperçu et le choix du nombre de copies restent visibles pour tous)
//
// Les deux onglets sont visibles pour qui a accès à la page.
// Les boutons "Voir" (notification) et "Aperçu" (note) restent visibles pour tous.

// absences, CRUD des notes d'information).
import { useState, useEffect, useRef, useCallback } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function formatDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("fr-FR"); } catch { return d; }
}

// ─── CSS / HTML communs pour édition, aperçu, impression, PDF ─────────────
const CONTENT_CSS = `
  font-family: 'Times New Roman', Times, serif;
  font-size: 13pt;
  line-height: 1.8;
  color: #000;
`;

const SEPARATOR_HTML = `
  <div style="margin: 8px 0; border-top: 1.5px dashed #888;"></div>
`;

const buildPrintCSS = () => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Times New Roman', Times, serif;
    background: white;
    color: #000;
    padding: 20mm 15mm;
  }
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .bloc-note { page-break-inside: avoid; break-inside: avoid; }
  .separateur { page-break-before: avoid; break-before: avoid; page-break-after: avoid; break-after: avoid; }
  @media print {
    body { padding: 10mm 12mm; }
    @page { size: A4 portrait; margin: 0; }
  }
`;

const buildPrintBody = (contenu, count) => {
  let html = "";
  for (let i = 0; i < count; i++) {
    html += `
      <div class="bloc-note" style="${CONTENT_CSS} padding: 10px 0 14px;">
        ${contenu}
      </div>`;
    if (i < count - 1) {
      html += `<div class="separateur" style="margin: 8px 0; border-top: 1.5px dashed #888;"></div>`;
    }
  }
  return html;
};

// ─── Éditeur riche (isolé via useRef pour éviter le conflit React/contentEditable) ─
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const execCmd = (e, cmd, val) => {
    e.preventDefault();
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, val || null);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const changeFontSize = (e, direction) => {
    e.preventDefault();
    if (!editorRef.current) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

    const range = sel.getRangeAt(0);
    const parentSpan = sel.anchorNode?.parentElement?.closest("span[style*='font-size']");
    let currentSize = 13;
    if (parentSpan) {
      const match = parentSpan.style.fontSize.match(/(\d+(\.\d+)?)/);
      if (match) currentSize = parseFloat(match[1]);
    }
    const newSize = Math.max(6, Math.min(72, direction === "up" ? currentSize + 1 : currentSize - 1));

    try {
      const span = document.createElement("span");
      span.style.fontSize = `${newSize}pt`;
      range.surroundContents(span);
      const nr = document.createRange();
      nr.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(nr);
    } catch {
      document.execCommand("fontSize", false, "7");
      editorRef.current.querySelectorAll("font[size='7']").forEach((el) => {
        const s = document.createElement("span");
        s.style.fontSize = `${newSize}pt`;
        s.innerHTML = el.innerHTML;
        el.replaceWith(s);
      });
    }
    onChange(editorRef.current.innerHTML);
  };

  const toolbarButtons = [
    ["<b>G</b>", "bold", "Gras"],
    ["<i>I</i>", "italic", "Italique"],
    ["<u>S</u>", "underline", "Souligné"],
    ["¶", "formatBlock", "Paragraphe", "p"],
    ["•", "insertUnorderedList", "Liste à puces"],
    ["→", "indent", "Indenter"],
    ["←", "outdent", "Désindenter"],
    ["⎯", "insertHorizontalRule", "Ligne horizontale"],
  ];

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {toolbarButtons.map(([label, cmd, title, val]) => (
          <button
            key={cmd + label}
            title={title}
            onMouseDown={(e) => execCmd(e, cmd, val)}
            className="rte-btn"
            dangerouslySetInnerHTML={{ __html: label }}
          />
        ))}
        <span className="rte-sep" />
        <button title="Diminuer la taille" onMouseDown={(e) => changeFontSize(e, "down")} className="rte-btn">
          A<sup style={{ fontSize: 9 }}>−</sup>
        </button>
        <button title="Augmenter la taille" onMouseDown={(e) => changeFontSize(e, "up")} className="rte-btn">
          A<sup style={{ fontSize: 9 }}>+</sup>
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rte-area"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
      />
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADNotifications({ slug }) {
  const [activeTab, setActiveTab] = useState("notifications");

  return (
    <div>
      {/* Onglets comme dans Configuration */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "notifications" ? "active" : ""}`}
          onClick={() => setActiveTab("notifications")}
        >
          <i className="ti ti-bell" /> NOTIFICATIONS
        </button>
        <button
          className={`tab-btn ${activeTab === "notes" ? "active" : ""}`}
          onClick={() => setActiveTab("notes")}
        >
          <i className="ti ti-notes" /> NOTES D'INFORMATION
        </button>
      </div>


      {activeTab === "notifications" && <NotificationsPanel slug={slug} />}
      {activeTab === "notes" && <NotesInformationPanel slug={slug} />}

      <style>{`
/* ─── ONGLETS (thème bleu + or) ─── */
.tab-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-radius: 10px;
  border-bottom: 3px solid var(--or);
  padding: 8px 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  color: rgba(255,255,255,0.75);
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.tab-btn i { font-size: 18px; }

.tab-btn:hover {
  background: rgba(255,255,255,0.12);
  color: var(--or-lt);
}

.tab-btn.active {
  background: white;
  color: var(--pr);
  box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
}
.tab-btn.active i { color: var(--or); }
.tab-btn.active:hover { background: white; color: var(--pr); }
        .pill {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 500;
        }
        .pill.ok  { background: var(--gn-bg); color: var(--gn); }
        .pill.warn{ background: var(--am-bg); color: var(--am); }
        .pill.err { background: var(--rd-bg); color: var(--rd); }
        .pill.info{ background: var(--bl-bg); color: var(--bl-dk); }
        .pill.pu  { background: var(--pr-bg); color: var(--pr); }
        .pill.neu { background: var(--s2); color: var(--m); }

        .card {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          padding: 14px;
        }

        .bprim {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 15px;
          background: var(--pr);
          color: #fff;
          border: none;
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          font-weight: 500;
          white-space: nowrap;
          transition: opacity .12s;
        }
        .bprim:hover { opacity: .88; }
        .bprim.rd { background: var(--rd); }

        .bsec {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 13px;
          background: var(--s);
          color: var(--m);
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          transition: all .12s;
        }
        .bsec:hover { background: var(--s2); color: var(--t); }

        .sb2 {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 11px;
          flex-wrap: wrap;
        }
        .sb2 input {
          flex: 1;
          min-width: 120px;
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }
        .sb2 select {
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }

        .factions {
          display: flex;
          gap: 7px;
          margin-top: 12px;
          justify-content: flex-end;
        }

        .ibtn {
          width: 30px;
          height: 30px;
          border-radius: 5px;
          border: 0.5px solid var(--b);
          background: var(--s);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--m);
          font-size: 17px;
          transition: all .12s;
        }
        .ibtn:hover { background: var(--s2); color: var(--t); }

        .ch {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .ct {
          font-size: 16px;
          font-weight: 600;
          color: var(--t);
        }

        .tbl {
          width: 100%;
          border-collapse: collapse;
          font-size: 15px;
          table-layout: fixed;
        }
        .tbl th {
          text-align: left;
          padding: 9px 10px;
          color: var(--m);
          font-weight: 600;
          border-bottom: 0.5px solid var(--b);
          font-size: 14px;
          white-space: nowrap;
        }
        .tbl td {
          padding: 10px 10px;
          border-bottom: 0.5px solid var(--b);
          color: var(--t);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .tbl tr:last-child td { border-bottom: none; }
        .tbl tr:hover td { background: var(--s2); }

        .act {
          display: flex;
          gap: 3px;
        }
        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }
        .act button:hover {
          opacity: 0.8 !important;
        }
        .act button.del {
          background: var(--rd);
          color: white;
        }
        .act button.del:hover {
          background: var(--rd-dk);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }
        .modal-box {
          background: var(--s);
          border-radius: var(--radius-lg);
          border: 0.5px solid var(--b);
          padding: 18px;
          max-width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-title {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin-bottom: 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .fg {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .fg label {
          font-size: 14px;
          color: var(--m);
          font-weight: 500;
        }
        .fg input, .fg select, .fg textarea {
          padding: 9px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
          width: 100%;
        }
        .fg textarea {
          resize: vertical;
          min-height: 56px;
        }

        /* ─── Sélection de parents spécifiques ─── */
        .parent-picker-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 6px;
          margin-bottom: 2px;
        }

        .parent-picker-list {
          max-height: 260px;
          overflow-y: auto;
          border: 0.5px solid var(--b2);
          border-radius: 6px;
          padding: 2px;
          background: var(--s);
        }

        .parent-picker-row {
          display: flex;
          align-items: baseline;
          gap: 8px;
          padding: 6px 8px;
          margin: 0;
          cursor: pointer;
          border-radius: 4px;
          border-bottom: 0.5px solid var(--b);
          transition: background .12s;
          font-size: 13px;
          line-height: 1.3;
        }
        .parent-picker-row:last-child { border-bottom: none; }
        .parent-picker-row:hover { background: var(--s2); }
        .parent-picker-row.checked { background: var(--pr-bg); }
        .parent-picker-row.checked .parent-picker-nom { color: var(--pr); }

        .parent-picker-nom {
          font-weight: 600;
          color: var(--t);
          white-space: nowrap;
          flex-shrink: 0;
        }

        .parent-picker-tel {
          color: var(--m);
          white-space: nowrap;
          flex-shrink: 0;
        }

        .parent-picker-enfants {
          color: var(--m);
        }

        .parent-picker-empty {
          text-align: center;
          color: var(--h);
          padding: 12px;
          font-size: 13px;
        }

        /* ─── NOTES D'INFORMATION : cartes dépliables ─── */
        .note-card {
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          margin-bottom: 12px;
          overflow: hidden;
          background: var(--s);
        }
        .note-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--s2);
          cursor: pointer;
          user-select: none;
        }
        .note-card-title { font-weight: 700; color: var(--pr); font-size: 15px; }
        .note-card-meta { font-size: 12px; color: var(--m); margin-top: 3px; }
        .note-card-body { padding: 16px; }
        .note-preview {
          border-top: 1px dashed var(--b2);
          padding-top: 12px;
          margin-top: 2px;
          font-family: 'Times New Roman', Times, serif;
          font-size: 13pt;
          line-height: 1.8;
          color: var(--t);
        }
        .note-actions {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          align-items: center;
          margin-top: 14px;
        }
        .note-copies {
          display: flex;
          align-items: center;
          gap: 7px;
          background: var(--pr-bg);
          border-radius: 7px;
          padding: 6px 11px;
          font-size: 13px;
          color: var(--pr);
          font-weight: 600;
        }
        .note-copies input {
          width: 46px;
          text-align: center;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          padding: 3px 6px;
          font-size: 14px;
          font-weight: 700;
          color: var(--pr);
          background: var(--s);
        }
        .note-sbtn {
          display: flex; align-items: center; gap: 6px;
          border: none; border-radius: 6px; padding: 8px 13px;
          font-size: 13px; font-weight: 600; color: #fff; cursor: pointer;
          transition: filter .15s;
        }
        .note-sbtn:hover { filter: brightness(0.9); }
        .note-sbtn:disabled { opacity: 0.6; cursor: not-allowed; }
        .note-sbtn.info { background: #17a2b8; }
        .note-sbtn.purple { background: #6f42c1; }
        .note-sbtn.pdf { background: var(--rd); }
        .note-sbtn.edit { background: #e67e22; }
        .note-sbtn.del { background: var(--rd); }

        .note-tip {
          background: var(--am-bg);
          border: 0.5px solid #ffe082;
          border-radius: 8px;
          padding: 10px 14px;
          margin-bottom: 14px;
          font-size: 13px;
          color: var(--am);
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        /* ─── Éditeur riche ─── */
        .rte { border: 0.5px solid var(--b2); border-radius: 5px; overflow: hidden; background: var(--s); }
        .rte-toolbar {
          display: flex; gap: 4px; padding: 6px 10px; flex-wrap: wrap;
          background: var(--pr-bg); border-bottom: 0.5px solid var(--b2); align-items: center;
        }
        .rte-btn {
          background: var(--s); border: 0.5px solid var(--b2);
          border-radius: 4px; padding: 3px 8px; cursor: pointer;
          font-size: 13px; font-weight: 600; color: var(--t);
          min-width: 30px; text-align: center; line-height: 1.4;
        }
        .rte-sep { width: 1px; height: 22px; background: var(--b2); margin: 0 4px; display: inline-block; }
        .rte-area {
          min-height: 200px;
          padding: 14px 18px;
          font-family: 'Times New Roman', Times, serif;
          font-size: 13pt;
          line-height: 1.8;
          color: var(--t);
          outline: none;
          background: var(--s);
        }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════════
function NotificationsPanel({ slug }) {
  const { canSee } = usePermissions();
  const peutEnvoyer = canSee("notifications.envoyer");
  const peutNotifierAbsences = canSee("notifications.notifier_absences");
  const peutSupprimer = canSee("notifications.supprimer");

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNouvelle, setShowNouvelle] = useState(false);
  const [showAbsences, setShowAbsences] = useState(false);
  const [selected, setSelected] = useState(null);

  const toast = useToastHook();
  const { getNotifications, deleteNotification } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getNotifications(slug);
      setNotifications(data || []);
    } catch (error) {
      toast.error("Impossible de charger les notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!peutSupprimer) return;
    if (!window.confirm("Supprimer cette notification ?")) return;
    try {
      await deleteNotification(slug, id);
      toast.success("Notification supprimée");
      load();
    } catch (error) {
      toast.error("Impossible de supprimer la notification");
    }
  };

  return (
    <div className="card">
      <div className="ch">
        <div className="ct">Notifications envoyées</div>
        <div style={{ display: "flex", gap: 7 }}>
          {/* notifications.notifier_absences */}
          {peutNotifierAbsences && (
            <button className="bsec" onClick={() => setShowAbsences(true)}>
              <i className="ti ti-user-exclamation" /> Notifier les absences
            </button>
          )}
          {/* notifications.envoyer */}
          {peutEnvoyer && (
            <button className="bprim" onClick={() => setShowNouvelle(true)}>
              <i className="ti ti-plus" /> Nouvelle notification
            </button>
          )}
        </div>
      </div>

      {loading ? <Loader /> : (
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Titre</th>
              <th style={{ width: "15%" }}>Destinataires</th>
              <th style={{ width: "15%" }}>Date d'envoi</th>
              <th style={{ width: "5%" }}></th>
            </tr>
          </thead>
          <tbody>
            {notifications.map(n => (
              <tr key={n.id}>
                <td>{n.titre}</td>
                <td>{(n.destinataires || []).length}</td>
                <td>{formatDateTime(n.date_envoi)}</td>
                <td>
                  <div className="act">
                    {/* Voir : visible pour tous */}
                    <button
                      onClick={() => setSelected(n)}
                      title="Voir"
                      style={{ background: "#17a2b8", color: "white" }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <i className="ti ti-eye" />
                    </button>
                    {/* notifications.supprimer */}
                    {peutSupprimer && (
                      <button
                        className="del"
                        onClick={() => handleDelete(n.id)}
                        title="Supprimer"
                      >
                        <i className="ti ti-trash" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {notifications.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
                Aucune notification envoyée
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {peutEnvoyer && showNouvelle && (
        <NouvelleNotificationModal
          slug={slug}
          onClose={() => setShowNouvelle(false)}
          onCree={() => { setShowNouvelle(false); load(); }}
        />
      )}

      {peutNotifierAbsences && showAbsences && (
        <NotifierAbsencesModal slug={slug} onClose={() => setShowAbsences(false)} />
      )}

      {selected && (
        <Modal title={selected.titre} onClose={() => setSelected(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", marginBottom: 8 }}>
            Envoyée le {formatDateTime(selected.date_envoi)} à {(selected.destinataires || []).length} destinataire(s)
          </div>
          <div style={{ fontSize: 15, whiteSpace: "pre-wrap" }}>{selected.message}</div>
          {selected.fichier && (
            <a href={selected.fichier} target="_blank" rel="noreferrer" style={{ display: "block", marginTop: 10, fontSize: 14 }}>
              <i className="ti ti-paperclip" /> Pièce jointe
            </a>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── Nouvelle notification : portée école / classe / parents spécifiques ──
function NouvelleNotificationModal({ slug, onClose, onCree }) {
  const [portee, setPortee] = useState("ecole"); // 'ecole' | 'classe' | 'specifique'
  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState("");
  const [destinataires, setDestinataires] = useState([]);
  const [loadingDest, setLoadingDest] = useState(false);
  const [titre, setTitre] = useState("");
  const [message, setMessage] = useState("");
  const [fichier, setFichier] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);

  // Sélection manuelle de parents (portée "specifique")
  const [tousParents, setTousParents] = useState([]);
  const [parentsCharges, setParentsCharges] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  const [searchParent, setSearchParent] = useState("");
  const [parentsSelectionnes, setParentsSelectionnes] = useState([]); // ids

  const toast = useToastHook();
  const { getNouveauMessageData, getParents, createNotification } = useAdminService();

  useEffect(() => { initClasses(); }, []);

  useEffect(() => {
    if (portee === "ecole" || portee === "classe") {
      computeDestinataires();
    } else if (portee === "specifique") {
      loadTousParents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portee, classeId]);

  // Synchronise `destinataires` avec la sélection manuelle pour la portée "specifique"
  useEffect(() => {
    if (portee === "specifique") setDestinataires(parentsSelectionnes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentsSelectionnes, portee]);

  const initClasses = async () => {
    setLoadingClasses(true);
    try {
      const res = await getNouveauMessageData(slug);
      setClasses(res.classes || []);
    } catch (error) {
      toast.error("Impossible de charger les classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  const computeDestinataires = async () => {
    setLoadingDest(true);
    try {
      if (portee === "ecole") {
        const res = await getParents(slug);
        const list = Array.isArray(res) ? res : (res.results || []);
        setDestinataires(list.map(p => p.id));
      } else if (portee === "classe" && classeId) {
        const res = await getNouveauMessageData(slug, { classe_id: classeId });
        const ids = (res.eleves || [])
          .map(e => e.parent?.id)
          .filter(Boolean);
        setDestinataires([...new Set(ids)]);
      } else {
        setDestinataires([]);
      }
    } catch (error) {
      toast.error("Impossible de déterminer les destinataires");
      setDestinataires([]);
    } finally {
      setLoadingDest(false);
    }
  };

  // Charge tous les parents avec leurs enfants (parents/details/, page_size=all)
  // pour permettre une sélection manuelle facilement identifiable.
  const loadTousParents = async () => {
    if (parentsCharges) return; // déjà chargé, on ne recharge pas
    setLoadingParents(true);
    try {
      const res = await getParents(slug, { page_size: 'all' });
      const list = Array.isArray(res) ? res : (res.results || []);
      setTousParents(list);
      setParentsCharges(true);
    } catch (error) {
      toast.error("Impossible de charger la liste des parents");
    } finally {
      setLoadingParents(false);
    }
  };

  const parentsFiltres = tousParents.filter(p => {
    if (!searchParent.trim()) return true;
    const q = searchParent.toLowerCase();
    const nomParent = `${p.first_name || ""} ${p.last_name || ""} ${p.username || ""}`.toLowerCase();
    const tel = `${p.telephone1 || ""}${p.telephone2 || ""}`;
    const nomsEnfants = (p.enfants || []).map(e => `${e.prenom || ""} ${e.nom || ""}`).join(" ").toLowerCase();
    return nomParent.includes(q) || tel.includes(q) || nomsEnfants.includes(q);
  });

  const toggleParent = (id) => {
    setParentsSelectionnes(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toutSelectionne = parentsFiltres.length > 0 && parentsFiltres.every(p => parentsSelectionnes.includes(p.id));

  const toggleTous = () => {
    const visibles = parentsFiltres.map(p => p.id);
    if (toutSelectionne) {
      setParentsSelectionnes(prev => prev.filter(id => !visibles.includes(id)));
    } else {
      setParentsSelectionnes(prev => [...new Set([...prev, ...visibles])]);
    }
  };

  const handlePorteeChange = (val) => {
    setPortee(val);
    setClasseId("");
    if (val !== "specifique") setParentsSelectionnes([]);
  };

  const handleEnvoyer = async () => {
    if (!titre.trim() || !message.trim()) { toast.error("Titre et message requis"); return; }
    if (destinataires.length === 0) { toast.error("Aucun destinataire pour cette sélection"); return; }
    setSending(true);
    try {
      await createNotification(slug, { titre, message, destinataires }, fichier);
      toast.success("Notification envoyée");
      onCree();
    } catch (error) {
      toast.error("Impossible d'envoyer la notification");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title="Nouvelle notification" onClose={onClose} maxWidth={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Portée</label>
          <select value={portee} onChange={e => handlePorteeChange(e.target.value)}>
            <option value="ecole">Toute l'école (tous les parents)</option>
            <option value="classe">Une classe précise</option>
            <option value="specifique">Parents spécifiques</option>
          </select>
        </div>

        {portee === "classe" && (
          <div className="fg">
            <label>Classe</label>
            {loadingClasses ? <Loader /> : (
              <select value={classeId} onChange={e => setClasseId(e.target.value)}>
                <option value="">-- Choisir une classe --</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            )}
          </div>
        )}

        {portee === "specifique" && (
          <div className="fg">
            <label>Rechercher un parent ou un enfant</label>
            <input
              type="text"
              placeholder="Nom du parent, de l'enfant, ou téléphone…"
              value={searchParent}
              onChange={e => setSearchParent(e.target.value)}
            />

            {loadingParents ? <Loader /> : (
              <>
                <div className="parent-picker-head">
                  <span style={{ fontSize: 12, color: "var(--m)" }}>
                    {parentsFiltres.length} parent(s) trouvé(s)
                  </span>
                  <button
                    type="button"
                    className="bsec"
                    style={{ padding: "4px 9px", fontSize: 12 }}
                    onClick={toggleTous}
                    disabled={parentsFiltres.length === 0}
                  >
                    {toutSelectionne ? "Tout désélectionner" : "Tout sélectionner"}
                  </button>
                </div>

                <div className="parent-picker-list">
                  {parentsFiltres.map(p => {
                    const nom = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Nom non renseigné";
                    const enfants = p.enfants || [];
                    const checked = parentsSelectionnes.includes(p.id);
                    const telephones = [p.telephone1, p.telephone2].filter(Boolean);
                    const nomsEnfants = enfants.map(e => `${e.prenom} ${e.nom}`).join(", ");

                    return (
                      <div
                        key={p.id}
                        className={`parent-picker-row${checked ? " checked" : ""}`}
                        onClick={() => toggleParent(p.id)}
                      >
                        <span className="parent-picker-nom">{nom}</span>
                        {telephones.length > 0 && (
                          <span className="parent-picker-tel">{telephones.join(" · ")}</span>
                        )}
                        {nomsEnfants && (
                          <span className="parent-picker-enfants">{nomsEnfants}</span>
                        )}
                      </div>
                    );
                  })}
                  {parentsFiltres.length === 0 && (
                    <div className="parent-picker-empty">Aucun parent trouvé</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ fontSize: 13, color: "var(--m)" }}>
          {loadingDest ? "Calcul des destinataires…" : `${destinataires.length} destinataire(s) sélectionné(s)`}
        </div>

        <div className="fg">
          <label>Titre</label>
          <input type="text" value={titre} onChange={e => setTitre(e.target.value)} />
        </div>

        <div className="fg">
          <label>Message</label>
          <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} />
        </div>

        <div className="fg">
          <label>Pièce jointe (optionnel)</label>
          <input type="file" onChange={e => setFichier(e.target.files?.[0] || null)} />
        </div>
      </div>

      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={sending}>Annuler</button>
        <button className="bprim" onClick={handleEnvoyer} disabled={sending || loadingDest}>
          {sending ? "..." : <><i className="ti ti-send" />Envoyer</>}
        </button>
      </div>
    </Modal>
  );
}

function NotifierAbsencesModal({ slug, onClose }) {
  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState("");
  const [dateCible, setDateCible] = useState(() => new Date().toISOString().slice(0, 10));
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [sending, setSending] = useState(false);
  const [resultat, setResultat] = useState(null);

  const toast = useToastHook();
  const { getNouveauMessageData, notifierAbsences } = useAdminService();

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoadingClasses(true);
    try {
      const res = await getNouveauMessageData(slug);
      setClasses(res.classes || []);
    } catch (error) {
      toast.error("Impossible de charger les classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleEnvoyer = async () => {
    setSending(true);
    setResultat(null);
    try {
      const payload = { date: dateCible };
      if (classeId) payload.classe_id = classeId;
      const res = await notifierAbsences(slug, payload);
      setResultat(res);
      toast.success("Notifications d'absences envoyées");
    } catch (error) {
      toast.error("Impossible d'envoyer les notifications d'absences");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title="Notifier les absences" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Date</label>
          <input type="date" value={dateCible} onChange={e => setDateCible(e.target.value)} />
        </div>

        <div className="fg">
          <label>Classe (optionnel — laisser vide pour toute l'école)</label>
          {loadingClasses ? <Loader /> : (
            <select value={classeId} onChange={e => setClasseId(e.target.value)}>
              <option value="">-- Toute l'école --</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          )}
        </div>

        {resultat && (
          <div style={{ fontSize: 13, color: "var(--m)" }}>
            {resultat.envoyees} / {resultat.total} notification(s) envoyée(s)
          </div>
        )}
      </div>

      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={sending}>Fermer</button>
        <button className="bprim" onClick={handleEnvoyer} disabled={sending}>
          {sending ? "..." : <><i className="ti ti-send" />Envoyer</>}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NOTES D'INFORMATION — logique alignée sur NoteInformationPage
// (édition riche, copies, aperçu, impression, PDF)
// ═══════════════════════════════════════════════════════════════════════
function NotesInformationPanel({ slug }) {
  const { canSee } = usePermissions();
  const peutCreerNote = canSee("notifications.creer_note");
  const peutModifierNote = canSee("notifications.modifier_note");
  const peutSupprimerNote = canSee("notifications.supprimer_note");
  const peutImprimerNote = canSee("notifications.imprimer_note");

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [copies, setCopies] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(null);

  const toast = useToastHook();
  const { getNotesInformation, deleteNoteInformation } = useAdminService();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotesInformation(slug);
      setNotes(data || []);
    } catch (error) {
      toast.error("Impossible de charger les notes d'information");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => { if (slug) load(); }, [slug, load]);

  const notesFiltrees = notes.filter(n => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (n.titre || "").toLowerCase().includes(q) || (n.contenu || "").toLowerCase().includes(q);
  });

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const getCopies = (id) => copies[id] || 3;
  const setCopyCount = (id, val) => {
    const n = Math.max(1, Math.min(50, parseInt(val) || 1));
    setCopies(p => ({ ...p, [id]: n }));
  };

  const handleDelete = async (id) => {
    if (!peutSupprimerNote) return;
    if (!window.confirm("Supprimer cette note d'information ?")) return;
    try {
      await deleteNoteInformation(slug, id);
      toast.success("Note supprimée");
      load();
    } catch (error) {
      toast.error("Impossible de supprimer la note");
    }
  };

  // ── Aperçu dans une nouvelle fenêtre ────────────────────────────────────
  const handleApercu = (note, count) => {
    const w = window.open("", "_blank", "width=850,height=750,scrollbars=yes");
    w.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Aperçu — ${note.titre} (${count}x)</title>
  <style>
    ${buildPrintCSS()}
    body { background: #e8e8e8; padding: 0; }
    .feuille {
      background: white;
      width: 210mm;
      min-height: 297mm;
      margin: 20px auto;
      padding: 20mm 15mm;
      box-shadow: 0 4px 24px rgba(0,0,0,0.18);
    }
  </style>
</head>
<body>
  <div class="feuille">
    ${buildPrintBody(note.contenu, count)}
  </div>
</body>
</html>`);
    w.document.close();
  };

  // ── Imprimer directement (N copies sur la même feuille A4) ─────────────
  const handlePrint = (note) => {
    if (!peutImprimerNote) return;
    const count = getCopies(note.id);
    const w = window.open("", "_blank", "width=850,height=750,scrollbars=yes");
    w.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${note.titre}</title>
  <style>${buildPrintCSS()}</style>
</head>
<body>
  ${buildPrintBody(note.contenu, count)}
  <script>
    window.onload = function () {
      setTimeout(function () { window.print(); }, 400);
    };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`);
    w.document.close();
    w.focus();
  };

  // ── Télécharger PDF ──────────────────────────────────────────────────────
  const handlePDF = async (note) => {
    if (!peutImprimerNote) return;
    setGeneratingPDF(note.id);
    try {
      const count = getCopies(note.id);

      const container = document.createElement("div");
      container.style.cssText = [
        "position: fixed",
        "left: -9999px",
        "top: 0",
        "width: 794px",
        "background: white",
        "padding: 28px 40px",
        "box-sizing: border-box",
        "font-family: 'Times New Roman', Times, serif",
        "font-size: 13pt",
        "color: #000",
      ].join(";");

      container.innerHTML = buildPrintBody(note.contenu, count);
      document.body.appendChild(container);

      await new Promise((r) => setTimeout(r, 400));

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: 794,
        windowWidth: 794,
        scrollX: 0,
        scrollY: 0,
        logging: false,
      });

      document.body.removeChild(container);

      const pdf = new jsPDF("portrait", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pdfW) / canvas.width;

      let posY = 0;
      while (posY < imgH) {
        const sliceH = Math.min(pdfH, imgH - posY);
        const srcY = (posY / imgH) * canvas.height;
        const srcH = (sliceH / imgH) * canvas.height;

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.ceil(srcH);
        slice.getContext("2d").drawImage(
          canvas,
          0, srcY, canvas.width, slice.height,
          0, 0, canvas.width, slice.height,
        );

        const imgData = slice.toDataURL("image/png", 1.0);
        if (posY > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfW, sliceH, undefined, "FAST");

        posY += pdfH;
      }

      pdf.save(`${note.titre.replace(/\s+/g, "_")}_x${count}.pdf`);
      toast.success("PDF téléchargé avec succès");
    } catch (err) {
      toast.error("Impossible de générer le PDF : " + err.message);
    } finally {
      setGeneratingPDF(null);
    }
  };

  return (
    <div className="card">
      <div className="ch">
        <div className="ct">Notes d'information</div>
        {/* notifications.creer_note */}
        {peutCreerNote && (
          <button className="bprim" onClick={() => { setEditing(null); setShowForm(true); }}>
            <i className="ti ti-plus" /> Nouvelle note
          </button>
        )}
      </div>

      <div className="sb2">
        <input type="text" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="note-tip">
        <i className="ti ti-alert-triangle" style={{ marginTop: 2, flexShrink: 0 }} />
        <span>
          Choisissez le nombre de copies, puis cliquez sur <em>Aperçu</em> pour visualiser
          {peutImprimerNote && <>, <em>Imprimer</em> pour lancer l'impression ou <em>PDF</em> pour télécharger</>}.
          {" "}Les copies sont empilées sur la même feuille, séparées par des lignes de découpe ✂.
        </span>
      </div>

      {loading ? <Loader /> : notesFiltrees.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
          Aucune note d'information
        </div>
      ) : (
        notesFiltrees.map(note => {
          const isExpanded = expanded[note.id];
          const copieCount = getCopies(note.id);
          const isGenPDF = generatingPDF === note.id;

          return (
            <div key={note.id} className="note-card">
              <div className="note-card-header" onClick={() => toggleExpand(note.id)}>
                <div style={{ flex: 1 }}>
                  <div className="note-card-title">{note.titre}</div>
                  <div className="note-card-meta">
                    {formatDateTime(note.date_creation)}
                    {note.annee_scolaire_nom ? ` · ${note.annee_scolaire_nom}` : ""}
                    {" · "}
                    <span className={`pill ${note.est_active ? "ok" : "neu"}`}>
                      {note.est_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <i className={`ti ${isExpanded ? "ti-chevron-up" : "ti-chevron-down"}`} style={{ color: "var(--pr)", marginLeft: 12 }} />
              </div>

              {isExpanded && (
                <div className="note-card-body">
                  <div className="note-preview" dangerouslySetInnerHTML={{ __html: note.contenu }} />

                  <div className="note-actions">
                    <div className="note-copies">
                      <i className="ti ti-copy" />
                      <span>Copies :</span>
                      <input
                        type="number" min={1} max={50} value={copieCount}
                        onChange={e => setCopyCount(note.id, e.target.value)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span style={{ fontSize: 12 }}>/ feuille</span>
                    </div>

                    {/* Aperçu : visible pour tous */}
                    <button className="note-sbtn info" onClick={() => handleApercu(note, copieCount)}>
                      <i className="ti ti-eye" /> Aperçu ({copieCount}x)
                    </button>

                    {/* notifications.imprimer_note */}
                    {peutImprimerNote && (
                      <>
                        <button className="note-sbtn purple" onClick={() => handlePrint(note)}>
                          <i className="ti ti-printer" /> Imprimer ({copieCount}x)
                        </button>

                        <button className="note-sbtn pdf" disabled={isGenPDF} onClick={() => !isGenPDF && handlePDF(note)}>
                          <i className="ti ti-file-type-pdf" /> {isGenPDF ? "Génération..." : `PDF (${copieCount}x)`}
                        </button>
                      </>
                    )}

                    {/* notifications.modifier_note */}
                    {peutModifierNote && (
                      <button className="note-sbtn edit" onClick={() => { setEditing(note); setShowForm(true); }}>
                        <i className="ti ti-pencil" /> Modifier
                      </button>
                    )}

                    {/* notifications.supprimer_note */}
                    {peutSupprimerNote && (
                      <button className="note-sbtn del" onClick={() => handleDelete(note.id)}>
                        <i className="ti ti-trash" /> Supprimer
                      </button>
                    )}
                  </div>

                  <div style={{ fontSize: 12, color: "var(--m)", marginTop: 10, fontStyle: "italic" }}>
                    💡 {copieCount} copie{copieCount > 1 ? "s" : ""} empilée{copieCount > 1 ? "s" : ""} sur la même feuille,
                    séparée{copieCount > 1 ? "s" : ""} par des lignes de découpe ✂
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {showForm && (editing ? peutModifierNote : peutCreerNote) && (
        <NoteInformationFormModal
          slug={slug}
          note={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function NoteInformationFormModal({ slug, note, onClose, onSaved }) {
  const [titre, setTitre] = useState(note?.titre || "");
  const [contenu, setContenu] = useState(note?.contenu || "");
  const [estActive, setEstActive] = useState(note?.est_active ?? true);
  const [anneeActive, setAnneeActive] = useState(null);
  const [loadingAnnee, setLoadingAnnee] = useState(true);
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { getAnneesScolaires, createNoteInformation, updateNoteInformation } = useAdminService();

  useEffect(() => { initAnneeActive(); }, []);

  const initAnneeActive = async () => {
    setLoadingAnnee(true);
    try {
      const res = await getAnneesScolaires(slug);
      const list = Array.isArray(res) ? res : (res.results || []);
      // Si l'édition d'une note existante avait déjà une année, on la garde,
      // sinon on prend l'année scolaire active.
      // ✅ FIX : le champ s'appelle `actif` sur AnneeScolaire (comme partout
      // ailleurs dans l'admin) ; `est_active` ne matchait jamais, donc
      // l'année active n'était jamais trouvée et la note était enregistrée
      // avec annee_scolaire = null. On garde `est_active` en repli au cas où.
      const active = list.find(a => a.actif ?? a.est_active) || null;
      setAnneeActive(note?.annee_scolaire ? { id: note.annee_scolaire, nom: note.annee_scolaire_nom } : active);
    } catch (error) {
      toast.error("Impossible de charger l'année scolaire active");
    } finally {
      setLoadingAnnee(false);
    }
  };

  const handleSave = async () => {
    const contenuTexte = contenu.replace(/<[^>]*>/g, "").trim();
    if (!titre.trim() || !contenuTexte) { toast.error("Titre et contenu requis"); return; }
    setSaving(true);
    try {
      const data = {
        titre,
        contenu,
        annee_scolaire: anneeActive?.id || null,
        est_active: estActive,
      };
      if (note) await updateNoteInformation(slug, note.id, data);
      else await createNoteInformation(slug, data);
      toast.success(note ? "Note modifiée" : "Note créée");
      onSaved();
    } catch (error) {
      toast.error("Impossible d'enregistrer la note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={note ? "Modifier la note" : "Nouvelle note d'information"} onClose={onClose} maxWidth={720}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Titre</label>
          <input type="text" value={titre} onChange={e => setTitre(e.target.value)} />
        </div>

        <div className="fg">
          <label>Contenu</label>
          <RichEditor value={contenu} onChange={setContenu} />
          <div style={{ fontSize: 12, color: "var(--m)", marginTop: 4 }}>
            💡 Ce que vous voyez ici est exactement ce qui sera imprimé.
          </div>
        </div>

        <div style={{ fontSize: 13, color: "var(--m)" }}>
          {loadingAnnee
            ? "Chargement de l'année scolaire…"
            : anneeActive
              ? <>Année scolaire : <strong>{anneeActive.nom}</strong></>
              : "Aucune année scolaire active trouvée"}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
          <input type="checkbox" checked={estActive} onChange={e => setEstActive(e.target.checked)} />
          Note active
        </label>
      </div>

      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving || loadingAnnee}>
          {saving ? "..." : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}